/**
 * Which of several announcements over ONE discount speaks for it.
 *
 * ⚠️ THIS FILE HAS NO DEPENDENCIES ON PURPOSE. It is the one piece of `@marva/shared`
 * the web app imports into PRODUCT code, not just into a test: `active-sales.js` pulls
 * in `@aws-sdk/lib-dynamodb` and a configured DynamoDB client at load, so the agent's
 * reader can never be bundled for a browser. The tie-break below therefore had to move
 * out of it — a rule that decides which row the merchant edits and which row the agent
 * reads cannot exist as two copies that merely look alike. Keep it free of `require`.
 *
 * WHY A TIE-BREAK EXISTS AT ALL. `sk = SALE#{discountId}` (with `saleId = discountId`)
 * makes one live grant per discount the normal case, but rows minted before that key
 * carry random uuids — so a discount really can come back with an older `SALE#{uuid}`
 * grant and the current `SALE#{discountId}` one side by side. Phase 5 supports those
 * legacy sets rather than migrating them, which means SOMETHING has to choose, and the
 * choice has to be the same on both surfaces:
 *
 *   - the agent (`applyMentionRights`) reads the winner's `name`, `discountDisplay` and,
 *     when the discount row carries no code of its own, its `discountCode`;
 *   - the panel (`effectiveGrant`) edits wording on the winner and revokes by its
 *     `saleId`.
 *
 * Disagree and the merchant's words are saved to a row the agent never reads, while an
 * obsolete code from a superseded row is handed to a shopper. Both were live defects:
 * the reader sorted `startsAt` DESCENDING and then let the last `Map.set()` win, which
 * selected the OLDEST sibling, and the panel independently preferred the lower `saleId`.
 *
 * THE ORDER, applied to rows already judged equal on standing (the panel ranks
 * active > scheduled > none first; the reader has filtered to live grants before it gets
 * here, so every row it compares is already active):
 *
 *   1. The deterministic `SALE#{discountId}` row. It is what the current writer produces,
 *      it is the row `POST /sales` overwrites and the cascade delete addresses by key,
 *      and it is therefore the only row whose wording and code can still be current.
 *   2. Failing that, a row carrying a usable `saleId`. A verdict the merchant cannot
 *      revoke is the defect one step further in — and note rule 1 implies this one, since
 *      a deterministic row's `saleId` IS the discount id.
 *   3. Then the newest `startsAt`. Among legacy siblings, later is the merchant's more
 *      recent word on the sale. A missing `startsAt` sorts oldest, exactly as the
 *      reader's descending sort placed it.
 *   4. Then the lower `saleId`, so the answer is total and nothing depends on the order
 *      DynamoDB happened to scan the partition in.
 *
 * @module @marva/shared/sale-grant-precedence
 */

/**
 * Is this the row the current writer produces — `sk = SALE#{discountId}`, `saleId` and
 * `linkedPriceRuleId` both the discount's own id?
 *
 * Compared as strings because `linkedPriceRuleId` is written as a string but arrives as
 * a number on rows old enough to predate that, and `77 !== '77'` would quietly demote
 * the current row to a legacy one.
 */
function isDeterministicGrant(sale) {
  if (!sale) return false;
  const saleId = sale.saleId == null ? '' : String(sale.saleId);
  const linked = sale.linkedPriceRuleId == null ? '' : String(sale.linkedPriceRuleId);
  return saleId !== '' && saleId === linked;
}

/** A row we could revoke. A verdict we cannot reverse is not worth stating. */
function hasRevocationHandle(sale) {
  return Boolean(sale && sale.saleId);
}

/**
 * Negative when `a` should speak for the discount, positive when `b` should, zero only
 * for two rows that are indistinguishable by every rule above. Shaped as a comparator so
 * a caller can either fold over candidates (the reader) or sort them.
 */
function compareGrantPrecedence(a, b) {
  const deterministicA = isDeterministicGrant(a);
  const deterministicB = isDeterministicGrant(b);
  if (deterministicA !== deterministicB) return deterministicA ? -1 : 1;

  const handleA = hasRevocationHandle(a);
  const handleB = hasRevocationHandle(b);
  if (handleA !== handleB) return handleA ? -1 : 1;

  // Newest first. `localeCompare` on the raw strings, so a missing `startsAt` ('') sorts
  // oldest — the same place the reader's descending sort put it.
  const startA = a && a.startsAt ? String(a.startsAt) : '';
  const startB = b && b.startsAt ? String(b.startsAt) : '';
  if (startA !== startB) return startA > startB ? -1 : 1;

  const idA = a && a.saleId ? String(a.saleId) : '';
  const idB = b && b.saleId ? String(b.saleId) : '';
  if (idA !== idB) return idA < idB ? -1 : 1;
  return 0;
}

/**
 * The one row that speaks for this discount, folded out of however many arrived.
 * `undefined` in, or an empty list, gives `undefined` — there is no grant to speak with.
 */
function preferredGrant(candidates) {
  let best;
  for (const candidate of candidates || []) {
    if (!candidate) continue;
    if (!best || compareGrantPrecedence(candidate, best) < 0) best = candidate;
  }
  return best;
}

module.exports = {
  isDeterministicGrant,
  hasRevocationHandle,
  compareGrantPrecedence,
  preferredGrant
};
